import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Search, 
  User, 
  ShieldCheck, 
  Mail, 
  Key, 
  MoreHorizontal,
  Loader2,
  RefreshCw,
  UserPlus,
  Copy,
  Check
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { usersService } from "@/services/usersService";
import { rolesService } from "@/services/roles";
import { User as UserType } from "@/types/crm";
import { Role } from "@/types/roles";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface UsersManagementProps {
  slug: string;
  onLinkEmployee?: (user: UserType) => void;
}

export function UsersManagement({ slug, onLinkEmployee }: UsersManagementProps) {
  const [users, setUsers] = useState<UserType[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetResultOpen, setResetResultOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [generatedPass, setGeneratedPass] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const { toast } = useToast();

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes] = await Promise.all([
        usersService.getUsers(slug),
        rolesService.getRoles(slug)
      ]);
      setUsers(usersRes.data || []);
      setRoles(rolesRes.data || []);
    } catch (error) {
      console.error("Error loading users data:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los usuarios del sistema",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (slug) loadData();
  }, [slug]);

  const filteredUsers = users.filter((u: any) => {
    const searchLower = search.toLowerCase();
    const fullName = `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.toLowerCase();
    const emailMatch = u.email.toLowerCase().includes(searchLower);
    const nameMatch = fullName.includes(searchLower) || (u.name || "").toLowerCase().includes(searchLower);
    return emailMatch || nameMatch;
  });

  const getRoleName = (roleId: string) => {
    return roles.find(r => r.id === roleId)?.name || "Sin Rol";
  };

  const handlePrepareReset = (user: UserType) => {
    setSelectedUser(user);
    setResetConfirmOpen(true);
  };

  const handleConfirmReset = async () => {
    if (!selectedUser) return;
    
    // Obtener ID del usuario que realiza la acción
    let updaterId = "";
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        updaterId = JSON.parse(storedUser).id;
      }
    } catch (e) {
      console.warn("No se pudo obtener el ID del administrador actual");
    }

    const newPass = Math.random().toString(36).slice(-8).toUpperCase();
    try {
      setLoading(true);
      await usersService.resetPassword(selectedUser.id, slug, updaterId, newPass);
      setGeneratedPass(newPass);
      setResetConfirmOpen(false);
      setResetResultOpen(true);
    } catch (error) {
      console.error("Error resetting password:", error);
      toast({
        title: "Error",
        description: "No se pudo restablecer la contraseña. Contacta a soporte técnico.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPass);
    setIsCopied(true);
    toast({
      title: "¡Copiado!",
      description: "La contraseña ya está en tu portapapeles.",
    });
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <Card className="border-slate-200/60 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por email o nombre..."
              className="pl-9 h-11 bg-slate-50/50 border-slate-200"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200/60 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6">
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-16 bg-slate-50/50 animate-pulse rounded-lg border border-slate-100" />
              ))}
            </div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                <TableHead className="w-[300px]">Usuario</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol asignado</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center text-slate-500">
                    No se encontraron usuarios que coincidan con la búsqueda.
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((u: any) => {
                  const isActive = u.is_active === true || u.estado === 1;
                  const displayName = u.first_name 
                    ? `${u.first_name} ${u.last_name || ""}` 
                    : u.firstName 
                      ? `${u.firstName} ${u.lastName || ""}`
                      : (u.name || "Usuario");

                  return (
                    <TableRow key={u.id} className="group transition-colors">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9 border border-slate-200 ring-2 ring-white shadow-sm">
                            <AvatarImage src={u.avatar_url} />
                            <AvatarFallback className="bg-slate-100 text-slate-400">
                              <User className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-slate-900 text-sm">
                              {displayName}
                            </p>
                            {u.is_owner && (
                              <Badge className="bg-amber-50 text-amber-700 border-amber-100 text-[9px] h-4 px-1.5 font-bold uppercase tracking-wider">
                                Owner
                              </Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="h-3.5 w-3.5 opacity-40" />
                          {u.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-medium text-[11px] bg-blue-50/50 text-blue-700 border-blue-100/50">
                          <ShieldCheck className="h-3 w-3 mr-1 opacity-70" />
                          {u.role?.name || getRoleName(u.role_id || u.role?.id || u.id_rol || "")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {isActive ? (
                          <Badge className="bg-green-50 text-green-700 border-green-100 text-[10px] h-5">Activo</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] h-5">Inactivo</Badge>
                        )}
                      </TableCell>
                    <TableCell className="text-right">
                      <TooltipProvider>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-900">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel>Gestión de Cuenta</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handlePrepareReset(u)} className="gap-2">
                              <RefreshCw className="h-4 w-4" /> Restablecer Clave
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Diálogo de Confirmación */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent className="rounded-2xl border-none shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-slate-900">
              ¿Restablecer contraseña?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500 pt-2">
              Se generará una nueva contraseña temporal para <strong>{selectedUser?.email}</strong>. 
              La clave actual dejará de funcionar inmediatamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="pt-4">
            <AlertDialogCancel className="border-none bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl">
              No, cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReset}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl px-6"
            >
              Sí, restablecer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de Resultado */}
      <Dialog open={resetResultOpen} onOpenChange={setResetResultOpen}>
        <DialogContent className="max-w-md rounded-3xl border-none shadow-2xl p-0 overflow-hidden">
          <div className="bg-blue-600 p-8 text-white text-center flex flex-col items-center gap-4">
            <div className="bg-white/20 p-4 rounded-full">
              <Key className="h-10 w-10 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">¡Clave Restablecida!</h2>
              <p className="text-blue-100 text-sm mt-1">Copia la nueva contraseña y entrégasela al usuario.</p>
            </div>
          </div>
          
          <div className="p-8 space-y-6 bg-white">
            <div className="space-y-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Contraseña Temporal</p>
              <div className="bg-slate-50 border-2 border-slate-100 p-6 rounded-2xl flex items-center justify-center gap-4 group">
                <code className="text-3xl font-black text-slate-800 tracking-wider font-mono">
                  {generatedPass}
                </code>
                <Button 
                  onClick={copyPassword}
                  size="icon"
                  variant="ghost" 
                  className={`h-12 w-12 rounded-xl transition-all ${isCopied ? 'text-green-600 bg-green-50' : 'text-blue-600 hover:bg-blue-50'}`}
                >
                  {isCopied ? <Check className="h-6 w-6" /> : <Copy className="h-6 w-6" />}
                </Button>
              </div>
            </div>

            <Button 
              onClick={() => setResetResultOpen(false)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-14 rounded-2xl font-bold text-lg shadow-xl shadow-slate-200 transition-all active:scale-95"
            >
              Entendido
            </Button>
            
            <p className="text-[11px] text-slate-400 text-center italic">
              * Por seguridad, pide al usuario que cambie su clave al ingresar.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
